/**
 * Server state for the settings screens.
 *
 * Every read is a `queryOptions` object and every write is a mutation that invalidates the keys
 * its change could have altered. That invalidation is the whole pattern: nothing copies server
 * data into React state, so there is no second copy to keep in step. After a write, TanStack
 * Query refetches what it marked stale and every component showing that data re-renders itself.
 *
 * The keys are deliberately coarse. `["businesses"]` covers the list, and creating a business
 * also invalidates `["categories"]` because the backend seeds a new bucket's default categories
 * in the same request — a write in one place can make another query wrong, and saying so here is
 * cheaper than hunting a stale screen later.
 */

import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, unwrapEmpty } from "@/api/problem";
import type { components } from "@/api/schema";

export type Workspace = components["schemas"]["WorkspaceOut"];
export type Business = components["schemas"]["BusinessOut"];
export type Category = components["schemas"]["CategoryOut"];
export type BucketType = components["schemas"]["BucketType"];

// --- reads --------------------------------------------------------------------------------------

export const workspaceQuery = queryOptions({
  queryKey: ["workspace"],
  queryFn: async () => unwrap(await api.GET("/api/v1/workspace")),
});

export const businessesQuery = queryOptions({
  queryKey: ["businesses"],
  queryFn: async () => unwrap(await api.GET("/api/v1/businesses")),
});

/** Categories of one bucket: a business when `businessId` is given, the household otherwise. */
export function categoriesQuery(businessId: string | null) {
  return queryOptions({
    queryKey: ["categories", businessId ?? "household"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/categories", {
          params: {
            query: businessId
              ? { business_id: businessId }
              : { bucket_type: "household" as BucketType },
          },
        }),
      ),
  });
}

// --- writes -------------------------------------------------------------------------------------

export function useUpdateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name?: string; timezone?: string; keep_receipt_images?: boolean }) =>
      unwrap(await api.PATCH("/api/v1/workspace", { body })),
    onSuccess: async () => {
      // `me` carries the workspace name in the header, so it goes stale too.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["workspace"] }),
        queryClient.invalidateQueries({ queryKey: ["me"] }),
      ]);
    },
  });
}

export function useCreateBusiness() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) =>
      unwrap(await api.POST("/api/v1/businesses", { body: { name } })),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["businesses"] }),
        // A new business arrives with its default categories already created.
        queryClient.invalidateQueries({ queryKey: ["categories"] }),
      ]);
    },
  });
}

export function useUpdateBusiness() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; is_active?: boolean }) =>
      unwrap(
        await api.PATCH("/api/v1/businesses/{business_id}", {
          params: { path: { business_id: id } },
          body,
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["businesses"] });
    },
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, businessId }: { name: string; businessId: string | null }) =>
      unwrap(
        await api.POST("/api/v1/categories", {
          body: {
            name,
            bucket_type: (businessId ? "business" : "household") as BucketType,
            ...(businessId ? { business_id: businessId } : {}),
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useRenameCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      unwrap(
        await api.PATCH("/api/v1/categories/{category_id}", {
          params: { path: { category_id: id } },
          body: { name },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/v1/categories/{category_id}", {
          params: { path: { category_id: id } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

// --- closing the account ------------------------------------------------------------------------

/** Whether a full export was downloaded today, and whether Stripe will be cancelled. */
export const closingQuery = queryOptions({
  queryKey: ["account", "closing"],
  queryFn: async () => unwrap(await api.GET("/api/v1/account/closing")),
});

/**
 * Close the account. The server ends this session in the same response, so everything cached
 * belongs to an account that no longer exists: the cache is cleared, not invalidated, as on
 * sign-out.
 */
export function useCloseAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { confirm_email: string; skip_export: boolean }) =>
      unwrap(await api.POST("/api/v1/account/close", { body })),
    onSuccess: () => queryClient.clear(),
  });
}
